import { Settings } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { iconRailItems } from '@/shared/config/sidebar.config';
import { useSidebar } from '@/core/hooks/useSidebar';
import { useUIStore, type ActivePanelId } from '@/core/stores/ui.store';
import { cn } from '@/core/lib/utils';
import { useTranslation } from 'react-i18next';

export function SidebarIconRail() {
  const { t } = useTranslation('layout');
  const { closeMobile, isMobile } = useSidebar();
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const activePanelId = useUIStore((s) => s.activePanelId);
  const setActivePanelId = useUIStore((s) => s.setActivePanelId);

  const handlePanelChange = (panelId: ActivePanelId) => {
    const leavingSettings = activePanelId === 'settings' && panelId !== 'settings';
    setActivePanelId(panelId);
    if (isMobile) closeMobile();

    if (panelId === 'settings' && orgId) {
      navigate(`/${orgId}/settings`);
    } else if (leavingSettings && orgId) {
      navigate(`/${orgId}/members`);
    }
  };

  return (
    <div
      className={cn(
        'w-16 shrink-0 flex flex-col items-center py-3 gap-1 border-r border-border',
        isMobile ? 'bg-muted' : 'bg-muted/40',
      )}
    >
      {/* Nav icons */}
      <div className="flex flex-col items-center gap-1 flex-1 w-full px-2">
        {iconRailItems.map(({ id, icon: Icon, label }) => {
          const isActive = activePanelId === id;
          return (
            <Tooltip key={id}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handlePanelChange(id as ActivePanelId)}
                  aria-label={label}
                  className={cn(
                    'w-10 h-10 rounded-xl',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icon className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{t(`sidebar.rail.${id}`)}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>

      {/* Settings — anclado al fondo */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handlePanelChange('settings')}
            aria-label={t('sidebar.user.settings')}
            className={cn(
              'w-10 h-10 rounded-xl mb-1',
              activePanelId === 'settings'
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Settings className="w-5 h-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">{t('sidebar.user.settings')}</TooltipContent>
      </Tooltip>
    </div>
  );
}
